// Centralized storage utility for localStorage operations
// This module can later be enhanced with encryption or IndexedDB support

const STORAGE_PREFIX = 'payment_gateway_';

// Helper functions for localStorage operations
export const storage = {
  // Set item with prefix
  setItem: (key, value) => {
    try {
      const prefixedKey = STORAGE_PREFIX + key;
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(prefixedKey, serializedValue);
      return true;
    } catch (error) {
      secureLogger.error('Storage setItem error:', error);
      return false;
    }
  },

  // Get item with prefix
  getItem: (key, defaultValue = null) => {
    try {
      const prefixedKey = STORAGE_PREFIX + key;
      const item = localStorage.getItem(prefixedKey);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      secureLogger.error('Storage getItem error:', error);
      return defaultValue;
    }
  },

  // Remove item with prefix
  removeItem: (key) => {
    try {
      const prefixedKey = STORAGE_PREFIX + key;
      localStorage.removeItem(prefixedKey);
      return true;
    } catch (error) {
      secureLogger.error('Storage removeItem error:', error);
      return false;
    }
  },

  // Clear all items with prefix
  clear: () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      secureLogger.error('Storage clear error:', error);
      return false;
    }
  },

  // Check if localStorage is available
  isAvailable: () => {
    try {
      const testKey = '__storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }
};

// Security-aware storage for sensitive data
export const secureStorage = {
  // Only store non-sensitive data in localStorage
  // For sensitive data, use server-side storage or encrypted storage
  setItem: (key, value) => {
    // Check if data contains sensitive information
    const sensitiveKeys = ['password', 'token', 'otp', 'secret', 'key'];
    const keyLower = key.toLowerCase();
    
    if (sensitiveKeys.some(sensitive => keyLower.includes(sensitive))) {
      secureLogger.warn(`⚠️ SECURITY: Attempted to store sensitive data in localStorage: ${key}`);
      return false;
    }
    
    return storage.setItem(key, value);
  },

  getItem: storage.getItem,
  removeItem: storage.removeItem,
  clear: storage.clear,
  isAvailable: storage.isAvailable
};

// Configuration for localStorage fallback
export const STORAGE_CONFIG = {
  // Allow localStorage fallback only in development or when explicitly enabled
  allowLocalFallback: process.env.REACT_APP_ALLOW_LOCAL_FALLBACK === 'true' || 
                     process.env.NODE_ENV === 'development',
  
  // Maximum size for localStorage (5MB)
  maxSize: 5 * 1024 * 1024,
  
  // Check localStorage size
  getSize: () => {
    try {
      let total = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          total += localStorage[key].length + key.length;
        }
      }
      return total;
    } catch (error) {
      return 0;
    }
  }
};

export default storage;
